import IconButton from "@mui/material/IconButton";
import React from "react";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { Link as RouterLink } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { textStyle } from "src/styles/textstyle";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { getIsSignedIn } from "src/redux/reducers/user";
import { useGetHomesQuery } from "src/redux/service/home.service";

type MenuItem = {
  title: string;
  link: string;
  count?: number;
  badgeColor?: string;
  badgeTextColor?: string;
};

export default function HamburgerMenu() {
  const [isOpen, setIsOpen] = React.useState(false);
  const mobileTheme = useTheme();
  const isMobile = useMediaQuery(mobileTheme.breakpoints.down("sm"));
  const isSignedIn = getIsSignedIn();
  const matchedSearchQuery = `?search=${encodeURIComponent(
    JSON.stringify({ matched: true })
  )}`;
  const requestedSearchQuery = `?search=${encodeURIComponent(
    JSON.stringify({ requested: true })
  )}`;
  const incomingRequestsSearchQuery = `?search=${encodeURIComponent(
    JSON.stringify({ incomingRequests: true })
  )}`;

  const { data: matchedHomes } = useGetHomesQuery(matchedSearchQuery, {
    skip: !isSignedIn,
  });
  const { data: requestedHomes } = useGetHomesQuery(requestedSearchQuery, {
    skip: !isSignedIn,
  });
  const { data: incomingRequestHomes } = useGetHomesQuery(
    incomingRequestsSearchQuery,
    {
      skip: !isSignedIn,
    }
  );

  const menuItems: MenuItem[] = [
    ...(isSignedIn
      ? [
          { title: "My profile", link: "/mypage" },
          { title: "My home", link: "/homepage" },
          {
            title: "My matches",
            link: `/homes${matchedSearchQuery}`,
            count: matchedHomes?.length,
            badgeColor: "#3980D1",
            badgeTextColor: "#FFFFFF",
          },
          {
            title: "My requests",
            link: `/homes${requestedSearchQuery}`,
            count: requestedHomes?.length,
            badgeColor: "#D9534F",
            badgeTextColor: "#FFFFFF",
          },
          {
            title: "My incoming requests",
            link: `/homes${incomingRequestsSearchQuery}`,
            count: incomingRequestHomes?.length,
            badgeColor: "#F2C94C",
            badgeTextColor: "#111111",
          },
        ]
      : []),
    { title: "FAQ", link: "/faq#faq-main" },
    { title: "Articles", link: "/articles" },
    { title: "Press releases", link: "/press-releases" },
  ];

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setIsOpen(open);
    };

  const list = () => (
    <List
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%", // Make the list take up full drawer height
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        {menuItems.map(({ title, link, count, badgeColor, badgeTextColor }) => (
          <ListItem key={title} onClick={toggleDrawer(false)}>
            <ListItemText>
              <RouterLink to={link} style={{ textDecoration: "none" }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    align="left"
                    sx={{
                      ...textStyle,
                      "&:hover": {
                        textDecoration: "underline",
                        textDecorationColor: "black",
                      },
                    }}
                  >
                    {title}
                  </Typography>
                  {typeof count === "number" && (
                    <Box
                      component="span"
                      sx={{
                        minWidth: "1.5rem",
                        height: "1.5rem",
                        px: 0.75,
                        borderRadius: "999px",
                        backgroundColor: badgeColor || "#111",
                        color: badgeTextColor || "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {count}
                    </Box>
                  )}
                </Box>
              </RouterLink>
            </ListItemText>
          </ListItem>
        ))}
      </Box>

      <Box>
        <ListItem onClick={toggleDrawer(false)}>
          <ListItemText>
            <RouterLink
              to="/terms-and-condition"
              style={{ textDecoration: "none" }}
            >
              <Typography
                variant="h6"
                align="left"
                sx={{
                  ...textStyle,
                  "&:hover": {
                    textDecoration: "underline",
                    textDecorationColor: "black",
                  },
                }}
              >
                Terms and conditions
              </Typography>
            </RouterLink>
          </ListItemText>
        </ListItem>
      </Box>
    </List>
  );

  return (
    <>
      <IconButton
        edge="start"
        color="inherit"
        aria-label="menu"
        sx={{ mr: 2, color: "black" }}
        onClick={toggleDrawer(true)}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          style: {
            width: isMobile ? "fit-content" : "24rem",
            minWidth: isMobile ? "18rem" : undefined,
            maxWidth: isMobile ? "88vw" : "100vw",
            // backgroundColor: mainColor, // apply the backgroundColor to the Paper
          },
        }}
      >
        {list()}
      </Drawer>
    </>
  );
}
